import { AuthService } from '../auth.service';
import { CookieService } from 'ngx-cookie-service';

import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule,
  TouchedChangeEvent,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { Auth, CognitoUser } from '@aws-amplify/auth';

import { filter, map } from 'rxjs';

export const MODULES = [
  CommonModule,
  MatCardModule,
  MatButtonModule,
  MatIconModule,
  MatMenuModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatInputModule,
  MatCheckboxModule,
  MatMenuModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];

@Component({
  selector: 'app-auth-screen',
  templateUrl: './auth-screen.component.html',
  styleUrls: ['./auth-screen.component.scss'],
  imports: [...MODULES, ...COMPONENTS],
  animations: [
    trigger('phraseAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate(
          '400ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 0, transform: 'translateY(-10px)' })
        ),
      ]),
    ]),
  ],
})
export class AuthScreenComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  private auth = inject(AuthService);
  private cd = inject(ChangeDetectorRef);
  public router = inject(Router);
  public translate = inject(TranslateService);
  private formBuilder = inject(UntypedFormBuilder);
  private snackBar = inject(MatSnackBar);
  private cookieService = inject(CookieService);

  public hidePassword = signal(true);
  public signInIsRunning = signal(false);
  public wasSignInError = false;
  alreadyCheckedPrivacy = signal(false);
  wantNewPassword = signal(false);
  limitException = signal(false);
  forgotPasswordCode: string;
  expiredPassword = signal(false);
  upsSomethingWentWrong = false;
  userName = signal<string>(undefined);
  oldPassword: string;
  user: CognitoUser;
  language = signal<string>(undefined);

  forgotPassword = signal(false);
  resetPasswordWithoutCode = signal(false);
  loginError = signal(false);
  loginErrorUser = signal(false);
  loginErrorMessage = signal(false);
  accountNotActive = signal(false);

  errorMessage = signal<string>(undefined);
  passwordArray = signal([false, false, false, false]);

  public loginForm = this.formBuilder.group({
    username: ['', [Validators.email, Validators.required]],
    password: ['', Validators.required],
  });

  private loginFormStatus = toSignal(this.loginForm.statusChanges, {
    initialValue: this.loginForm.status,
  });

  loginFormInvalid = computed(() => this.loginFormStatus() === 'INVALID');
  loginFormTouched = toSignal(
    this.loginForm.events.pipe(
      filter((e): e is TouchedChangeEvent => e instanceof TouchedChangeEvent),
      map(e => e.touched)
    ),
    { initialValue: this.loginForm.touched }
  );

  newPasswordForm = this.formBuilder.group({
    username: ['', [Validators.email, Validators.required]],
  });
  forgotPasswordForm = this.formBuilder.group({
    code: ['', Validators.required],
    passwordNew: ['', Validators.required],
  });

  private passwordNewControlValue = toSignal(
    this.forgotPasswordForm.get('passwordNew').valueChanges,
    { initialValue: this.forgotPasswordForm.get('passwordNew').value }
  );

  passwordValid = computed(() => {
    const passwordArray = this.passwordArray();
    return (
      passwordArray.length !== 0 &&
      passwordArray[3] &&
      passwordArray[0] &&
      passwordArray[1] &&
      passwordArray[2] &&
      passwordArray[4]
    );
  });

  public catchphrases: any[] = [];

  private allYearPhrases = [
    {
      icon: 'satellite-dish',
      text: 'Präzise meteorologische Echtzeitdaten für operative Entscheidungen.',
      bgImage: '../../assets/images/ws_energy.jpg',
    },
    {
      icon: 'analytics',
      text: 'MeteoFocus ist speziell auf die Bedürfnisse von Winter- und Sommerdiensten für Städte, Gemeinden und Kommunen, Flughäfen sowie den Nah- und Fernverkehr zugeschnitten.',
      bgImage: 'assets/images/summerset_rainbow.jpg',
    },
  ];

  private winterPhrases = [
    {
      icon: 'bolt',
      text: 'Individuelle Wettervorhersagen zur Enteisung von Tragflächen und zur Unterstützung der Verkehrssicherheit von Flughäfen.',
      bgImage: '../../assets/images/winterset_plane.jpg',
    },
    {
      icon: 'road',
      text: 'Spezifische Wettervorhersagen für Winterdienste und Straßenreinigung inklusive 24/7 meteorologischen Support.',
      bgImage: 'assets/images/winterset_bsr.png',
    },
  ];

  private summerPhrases = [
    {
      icon: 'road',
      text: 'Gewitter-, Starkregen- und Dauerregen-Indizes liefern standortgenaue Frühwarnungen mit klaren Schwellenwerten und Zeitfenstern.',
      bgImage: '../../assets/images/summerset_storm.jpg',
    },
    {
      icon: 'sun',
      text: 'Erzeugungsprognosen für Wind- und Solarenergie, Meteorologische Daten zur Abschätzung des Strombedarfs, Individuelle Datenmodelle für passgenaue Nettolastprognosen, Probabilistische Vorhersagen (P10–P90) sowie Konfidenzindikatoren',
      bgImage: '../../assets/images/summerset_energy.jpg',
    },
    {
      icon: 'bolt',
      text: 'Ob Stadtfest, Open-Air-Konzert oder Sportveranstaltung – das Wetter entscheidet oft über Erfolg oder Abbruch.',
      bgImage: '../../assets/images/summerset_events.jpg',
    },
  ];

  private setSeasonalCatchphrases(): void {
    const today = new Date();
    const month = today.getMonth() + 1; // getMonth() ist 0-basiert (Januar = 0, Oktober = 9)

    const isWinter = month >= 10 || month < 5;

    if (isWinter) {
      this.catchphrases = [...this.allYearPhrases, ...this.winterPhrases];
    } else {
      this.catchphrases = [...this.allYearPhrases, ...this.summerPhrases];
    }
  }

  /*public catchphrases = [
    {
      icon: 'satellite-dish',
      text: 'Präzise meteorologische Echtzeitdaten für operative Entscheidungen.',
      bgImage: '../../assets/images/clouds-bg.jpg',
    },
    {
      icon: 'bolt',
      text: 'Individuelle Wettervorhersagen zur Enteisung von Tragflächen und zur Unterstützung der Verkehrssicherheit von Flüghäfen.',
      bgImage: '../../assets/images/winter_street.jpg',
    },
    {
      icon: 'road',
      text: 'Spezifische Wettervorhersagen für den Mobilitätsbereich - für Straßen, Brücken, Rad- und Fußwege, Schiene und Flughäfen.',
      bgImage: '../../assets/images/summerset_morningsun.jpg',
    },
    {
      icon: 'cloud-sun',
      text: 'Spezifische Wettervorhersagen für Winterdienste und Straßenreinigung inklusive 24/7 meteorologischen Support.',
      bgImage: 'assets/images/winter_street.jpg',
    },
    {
      icon: 'analytics',
      text: 'Visualisierte Telemetrie-Messwerte und synoptische Analysen.',
      bgImage: 'assets/images/summerset_rainbow.jpg',
    },
  ];*/

  public currentPhraseIndex = signal(0);
  private intervalId: any;

  constructor() {
    this.listenToValidPasswordChanges();
  }

  ngOnInit(): void {
    this.checkPrivacyCheck();
    this.auth.loginIsRunning.set(true);
    this.auth.setLanguage();
    this.language.set(this.auth.getLanguage());
    this.setSeasonalCatchphrases();
    this.intervalId = setInterval(() => {
      this.currentPhraseIndex.update(
        index => (index + 1) % this.catchphrases.length
      );
    }, 5000);
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  checkPrivacyCheck(): void {
    this.alreadyCheckedPrivacy.set(this.cookieService.check('privacy_check'));
  }

  async signIn(): Promise<void> {
    this.alreadyCheckedPrivacy.set(true);
    this.cookieService.set('privacy_check', 'YES');
    this.signInIsRunning.set(true);
    this.loginError.set(false);
    this.loginErrorUser.set(false);
    this.loginErrorMessage.set(false);
    try {
      const username = this.loginForm.get('username').value.toLowerCase();
      const password = this.loginForm.get('password').value;
      Auth.signIn(username, password).then(
        user => {
          this.signInIsRunning.set(false);
          if (user && user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            this.userName.set(username);
            this.forgotPassword.set(true);
            this.user = user;
            this.oldPassword = password;
            this.resetPasswordWithoutCode.set(true);
          } else {
            this.auth.handleAuthentication(user);
            this.router.navigate(['/callback']);
          }
        },
        err => {
          console.log('error signing in', err);
          switch (err.code) {
            case 'NotAuthorizedException':
              this.loginError.set(true);
              break;
            case 'UserNotFoundException':
              this.loginErrorUser.set(true);
              break;
            case 'LimitExceededException':
              this.limitException.set(true);
              break;
            case 'PasswordResetRequiredException':
              this.triggerWantedPassword();
              // this.passwordNew = '';
              this.forgotPasswordCode = '';
              this.userName.set(username);
              this.forgotPassword.set(true);
              break;
            default:
              this.errorMessage.set(err.code);
              this.loginErrorMessage.set(true);
          }

          this.wasSignInError = true;
          this.signInIsRunning.set(false);
        }
      );
    } catch (error) {
      console.log('error signing in', error);
      this.wasSignInError = true;
      this.signInIsRunning.set(false);
    }
  }

  changePassword(): void {
    const username = this.userName();
    const password = this.forgotPasswordForm.get('passwordNew').value;
    this.loginError.set(false);
    Auth.completeNewPassword(this.user, password).then(
      user => {
        this.forgotPassword.set(false);
        this.loginError.set(false);
        this.snackBar.open(
          'Sie haben Ihr Passwort erfolgreich geändert. Bitte melden Sie sich nun mit dem neuen Passwort an.',
          'OK',
          {
            duration: 10000,
            verticalPosition: 'top',
            panelClass: 'snackbar-info',
          }
        );
      },
      err => {
        switch (err.code) {
          case 'NotAuthorizedException':
            this.errorMessage.set('Der Account ist nicht aktiv');
            this.accountNotActive.set(true);
            break;
          default:
            this.errorMessage.set(err.code);
        }
        this.loginError.set(true);
        console.error('changePassword', err);
      }
    );
  }

  handleForgotPassword(): void {
    const username = this.userName();
    const code = this.forgotPasswordForm.get('code').value.toString();
    const password = this.forgotPasswordForm.get('passwordNew').value;
    this.loginError.set(false);
    Auth.forgotPasswordSubmit(username, code, password).then(
      () => {
        this.forgotPassword.set(false);
        this.loginError.set(false);
        this.snackBar.open(
          'Sie haben Ihr Passwort erfolgreich geändert. Bitte melden Sie sich nun mit dem neuen Passwort an.',
          'OK',
          {
            duration: 10000,
            verticalPosition: 'top',
            panelClass: 'snackbar-info',
          }
        );
      },
      err => {
        this.errorMessage.set(err.code);
        this.loginError.set(true);
        console.error('handleForgotPassword', err);
      }
    );
  }

  askForNewPassword(): void {
    this.signInIsRunning.set(false);
    const username = this.loginForm.get('username').value;
    this.wantNewPassword.set(true);
    this.newPasswordForm.controls['username'].setValue(username);
  }

  triggerWantedPassword(): void {
    const email = this.newPasswordForm.get('username').value;
    this.userName.set(email);
    Auth.forgotPassword(this.userName())
      .then(data => {
        this.wantNewPassword.set(false);
        this.forgotPassword.set(true);
      })
      .catch(err => {
        console.log('triggerWantedPassword', err);
        if (err.code === 'NotAuthorizedException') {
          this.errorMessage.set('Der Account ist nicht aktiv');
          this.accountNotActive.set(true);
        } else if (err.name === 'LimitExceededException') {
          this.limitException.set(true);
          this.loginError.set(true);
        } else {
          this.errorMessage.set(err.name);
          this.loginError.set(true);
          console.error('triggerWantedPassword', err);
        }
      });
  }

  onContact(): void {
    const url =
      'https://www.meteofocus.com/feedback-' + this.auth.getLanguage() + '/';
    window.open(url, '_blank');
  }

  listenToValidPasswordChanges(): void {
    effect(() => {
      const numSet = '0123456789';
      const charKlein = 'abcdefghijklmnopqrstuvwxyz';
      const charGross = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const sonderChar = '!§$%&/()=?*_:;><,.-#+ß{}[]';
      const passwordNew = this.passwordNewControlValue();

      untracked(() => {
        if (!passwordNew) {
          this.passwordArray.set([false, false, false, false]);
          return;
        }
        for (let i = 0; i < numSet.length; i++) {
          if (passwordNew.indexOf(numSet[i]) >= 0) {
            this.passwordArray.update(passwordArray => {
              passwordArray[0] = true;
              return [...passwordArray];
            });
            break;
          } else {
            this.passwordArray.update(passwordArray => {
              passwordArray[0] = false;
              return [...passwordArray];
            });
          }
        }
        for (let i = 0; i < charKlein.length; i++) {
          if (passwordNew.indexOf(charKlein[i]) >= 0) {
            this.passwordArray.update(passwordArray => {
              passwordArray[1] = true;
              return [...passwordArray];
            });
            break;
          } else {
            this.passwordArray.update(passwordArray => {
              passwordArray[1] = false;
              return [...passwordArray];
            });
          }
        }
        for (let i = 0; i < charGross.length; i++) {
          if (passwordNew.indexOf(charGross[i]) >= 0) {
            this.passwordArray.update(passwordArray => {
              passwordArray[2] = true;
              return [...passwordArray];
            });
            break;
          } else {
            this.passwordArray.update(passwordArray => {
              passwordArray[2] = false;
              return [...passwordArray];
            });
          }
        }
        for (let i = 0; i < sonderChar.length; i++) {
          if (passwordNew.indexOf(sonderChar[i]) >= 0) {
            this.passwordArray.update(passwordArray => {
              passwordArray[3] = true;
              return [...passwordArray];
            });
            break;
          } else {
            this.passwordArray.update(passwordArray => {
              passwordArray[3] = false;
              return [...passwordArray];
            });
          }
        }
        this.passwordArray.update(passwordArray => {
          passwordArray[4] = passwordNew.length >= 8;
          return [...passwordArray];
        });
      });
    });
  }

  changeLanguage(language): void {
    localStorage.setItem('language', language);
    this.language.set(language);
    this.auth.setLanguage();
  }
}
